import React, { PureComponent } from 'react';
import { defineMessages, intlShape } from 'react-intl';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Downshift from 'downshift';
import { DESIGN_SYSTEM_ICONS } from 'constants/icons';
import { debounce } from 'helpers/debounce';
import { calculateTextWidth, getElementFont } from 'helpers/calculateTextWidth';
import { isEmpty } from 'helpers/verify';

const CURRENT_LOCATION = 'CURRENT_LOCATION';
const DEBOUNCE_MS = 250;
const MAX_SUGGESTIONS = 5;

const messages = defineMessages({
  currentLocation: {
    id: 'locationInput.currentLocation',
    defaultMessage: 'Current location',
  },
});

export class LocationInput extends PureComponent {
  constructor(props) {
    super(props);
    this.getInputValue = this.getInputValue.bind(this);
    this.handleInputStateChange = this.handleInputStateChange.bind(this);
    this.handleInputValueChange = this.handleInputValueChange.bind(this);
    this.handlePageLoaded = () => this.forceUpdate();
    this.handleSelection = this.handleSelection.bind(this);
    this.queryPlacesDebounced = debounce(this.props.queryPlaces, DEBOUNCE_MS);
  }

  state = {
    inputElement: null,
    inputValue: '',
    isActive: false,
  };

  componentDidMount() {
    const { inputLocation, onLoad } = this.props;

    if (onLoad) {
      onLoad(inputLocation);
    }

    if (typeof window !== 'undefined') {
      // Needed to correctly re-calculate the input width after the page has fully loaded.
      window.addEventListener('load', this.handlePageLoaded);
    }
  }

  componentDidUpdate(prevProps) {
    const { inputLocation, onChange } = this.props;

    if (inputLocation !== prevProps.inputLocation) {
      onChange(inputLocation);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('load', this.handlePageLoaded);
    }
  }

  getInputWidth() {
    const { style } = this.props;
    const { isActive } = this.state;
    const inputValue = this.getInputValue();

    if (typeof window !== 'undefined' && this.state.inputElement && style === 'underlined' && !isActive && inputValue) {
      const inputFont = getElementFont(this.state.inputElement);
      return calculateTextWidth(inputValue, inputFont);
    }
    return null;
  }

  getIcon(item) {
    return item.place_id === CURRENT_LOCATION ? DESIGN_SYSTEM_ICONS.currentLocation : DESIGN_SYSTEM_ICONS.pin;
  }

  getInputValue() {
    const { initialValue, inputLocation } = this.props;
    const { inputValue, isActive } = this.state;

    // eslint-disable-next-line no-nested-ternary
    return isActive ? inputValue : inputLocation ? inputLocation.name : initialValue;
  }

  getSuggestions() {
    const { hasDetectedLocation, intl, placeSuggestions } = this.props;

    return (hasDetectedLocation
      ? [
          {
            description: intl.formatMessage(messages.currentLocation),
            place_id: CURRENT_LOCATION,
          },
          ...placeSuggestions,
        ]
      : placeSuggestions
    ).slice(0, MAX_SUGGESTIONS);
  }

  handleInputStateChange(stateChanges) {
    if (stateChanges.isOpen === true) {
      this.setState({
        inputValue: '',
        isActive: true,
      });
    }

    if (stateChanges.isOpen === false || stateChanges.type === Downshift.stateChangeTypes.blurInput) {
      this.setState({ isActive: false });
    }
  }

  handleInputValueChange(query) {
    this.setState({ inputValue: query });
    this.queryPlacesDebounced(query);
  }

  handleSelection(place) {
    const { loadPlace, loadDetectedLocation, setInputSelection } = this.props;

    this.state.inputElement.blur();

    setInputSelection(place);
    if (place.place_id === CURRENT_LOCATION) {
      loadDetectedLocation();
    } else {
      loadPlace(place.place_id);
    }
  }

  itemToString(item) {
    return item ? item.description : '';
  }

  render() {
    const { label, inputAttributes, selectedItem, style } = this.props;

    const suggestions = this.getSuggestions();
    const extraClass = style === 'underlined' ? 'location-input--underlined' : null;

    return (
      <Downshift
        inputValue={this.getInputValue()}
        itemToString={this.itemToString}
        onSelect={this.handleSelection}
        onStateChange={this.handleInputStateChange}
        onInputValueChange={this.handleInputValueChange}
        selectedItem={selectedItem}>
        {({ getInputProps, getItemProps, getLabelProps, getMenuProps, highlightedIndex, isOpen, openMenu }) => (
          <div className={classNames('location-input', extraClass)}>
            {label && <label {...getLabelProps()}>{label}</label>}
            <div className="location-input__input-wrapper">
              <input
                {...getInputProps({
                  ...inputAttributes,
                  onFocus: openMenu,
                })}
                autoComplete="off"
                style={{ width: this.getInputWidth() }}
                ref={ref => this.setState({ inputElement: ref })}
              />
              {isOpen && !isEmpty(suggestions) && (
                <ul {...getMenuProps()}>
                  {suggestions.map((item, index) => (
                    <li
                      {...getItemProps({
                        key: item.place_id,
                        index,
                        item,
                      })}
                      className={classNames('location-input__item', {
                        'location-input__item--highlighted': highlightedIndex === index,
                      })}>
                      <Isvg className="location-input__icon" src={this.getIcon(item)} />
                      {item.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </Downshift>
    );
  }
}

LocationInput.propTypes = {
  hasDetectedLocation: PropTypes.bool,
  initialValue: PropTypes.string,
  inputAttributes: PropTypes.object,
  inputLocation: PropTypes.object,
  intl: intlShape,
  label: PropTypes.node,
  loadDetectedLocation: PropTypes.func.isRequired,
  loadPlace: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onLoad: PropTypes.func,
  placeSuggestions: PropTypes.array,
  queryPlaces: PropTypes.func.isRequired,
  selectedItem: PropTypes.object,
  setInputSelection: PropTypes.func.isRequired,
  style: PropTypes.oneOf(['underlined']),
};
